/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useCallback } from 'react';
// other libraries
import moment from 'moment';
import { isEmpty } from 'lodash';
// redux
import { getToDoNotificationsData } from '../../redux/slices/toDoNotifications';
import { dispatch, useSelector } from '../../redux/store';
import { getPracticeUserList } from '../../redux/slices/practiceUser';
import { emailCounter } from '../../redux/slices/emailCounter';
// hooks
import { useAuth } from '../../hooks';
// utils
import { getParsedData, localStorageGetItem, localStorageSetItem } from '../../utils/storage-available';
// notifications
import { useNotifications } from '../../sections/notifications/NotificationProvider';

export default function IntervalComponentV3() {
    const { isAuthenticated } = useAuth();
    const intervalDuration = 5 * 60 * 1000;
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const { toDoData, authFailed } = useSelector((state) => state.toDoNotifications);
    const { practiceUser } = useSelector((state) => state.practiceUser);
    const { user } = useAuth();
    const { showNotification } = useNotifications();

    // Track which reminder IDs we have already shown toasts for
    // to prevent re-showing them on every Redux state change.
    const shownIdsRef = useRef(new Set());

    const getAdminDetail = () => !isEmpty(practiceUser.results) ? practiceUser.results.find((users) => users.user?.email === user?.email) : null;

    useEffect(() => {
        if (practiceId) {
            dispatch(getPracticeUserList(practiceId));
            dispatch(emailCounter(practiceId));
        }
    }, [practiceId]);

    useEffect(() => {
        if (!isAuthenticated || !practiceId || !getAdminDetail()?.id) return;
        if (authFailed) return; // Don't restart polling after auth failure
        const INITIAL_DELAY_MS = 15000; // 15 seconds — let dashboard load first
        const lastStartTime = parseInt(localStorageGetItem('intervalStartTime'), 10);
        const currentTime = new Date().getTime();
        const elapsedTime = currentTime - lastStartTime;

        // If this is a fresh session (no previous interval), use the initial delay
        const isFirstPoll = !lastStartTime || Number.isNaN(elapsedTime) || elapsedTime > intervalDuration * 2;
        const remainingTime = isFirstPoll ? INITIAL_DELAY_MS : intervalDuration - (elapsedTime % intervalDuration);
        const intervalId = setTimeout(() => {
            handleApiCall();
            const nextIntervalId = setInterval(() => {
                handleApiCall();
            }, intervalDuration);

            localStorageSetItem('intervalId', nextIntervalId);
        }, remainingTime);

        localStorageSetItem('intervalStartTime', currentTime);

        localStorageSetItem('timeoutId', intervalId);

        return () => {
            const savedIntervalId = localStorageGetItem('intervalId');
            const timeoutId = localStorageGetItem('timeoutId');
            const timeoutIdV2 = localStorageGetItem('intervalIdV2');
            clearInterval(savedIntervalId);
            clearInterval(timeoutIdV2);
            clearTimeout(timeoutId);
            const snoozedData = getParsedData('timeout_ids') || [];
            if (!isEmpty(snoozedData)) {
                snoozedData?.forEach((element) => {
                    clearTimeout(element);
                });
            }
        };
    }, [isAuthenticated, practiceId, practiceUser, authFailed]);

    const handleApiCall = () => {
        // Stop polling if a 401 was received — token is expired/invalid
        if (authFailed) return;
        // Check auth freshness from token + closure to avoid firing with expired/missing tokens
        const token = localStorage.getItem('accessToken');
        if (!isAuthenticated || !token) return;
        const data = {
            startDate: moment().subtract(5, 'minutes').toISOString(),
            endDate: moment().add(5, 'minutes').toISOString(),
        };
        dispatch(getToDoNotificationsData(data));
    };

    // Show iOS toasts for new reminder items from the polling results
    const showToastsForReminders = useCallback((reminders) => {
        if (!reminders || reminders.length === 0) return;

        reminders.forEach((ele) => {
            // Skip if we already showed a toast for this reminder
            if (shownIdsRef.current.has(ele.id)) return;
            shownIdsRef.current.add(ele.id);

            const dateTimeString = moment(ele.dateTime, 'DD-MM-YYYY HH:mm:ss.SSSSSSZ');
            const timeStr = moment(dateTimeString, 'HH:mm').format('hh:mm A');

            showNotification({
                id: `reminder_${ele.id}`,
                verb: 'reminder_due_today',
                description: `${ele.method || 'Contact'} ${ele.firstName || ''} ${ele.lastName || ''} about ${ele.treatment || 'Treatment'} — Due today at ${timeStr}`,
                priority: 'urgent',
                requires_action: true,
                action_label: 'Open Task',
                action_link: `/dashboard/practice/${practiceId}/lead-detail/${ele.leadId}`,
                data: {
                    leadId: ele.leadId,
                    taskId: ele.id,
                    goToTaskTab: true,
                },
            });
        });
    }, [showNotification, practiceId]);

    // When toDoData changes (from polling), fire toasts for new items
    useEffect(() => {
        if (toDoData && toDoData.length > 0) {
            showToastsForReminders(toDoData);
        }
    }, [toDoData, showToastsForReminders]);

    // All rendering is handled by NotificationProvider / NotificationToastContainer
    return null;
}
